import { useDispatch, useSelector } from 'react-redux'
import { KibContainer, KibGrid, KibGridItem } from '@chewy/kib-layout-react'
import { type Field, type Nil, PermissionArea } from '@pbt/pbt-ui-components'

import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import EditableTitle from '~/components/dashboard/communications/common/EditableTitle'
import TransportIcon from '~/components/dashboard/communications/TransportIcon'
import FeatureToggle from '~/constants/featureToggle'
import {
  editConversation,
  setAssigneeForConversation,
} from '~/store/actions/conversations'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getFilesForMessageIds } from '~/store/reducers/conversationMessages'
import { getPatientName } from '~/store/reducers/patients'
import type { Conversation } from '~/types'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import KibEditableTitle from '../../common/KibEditableTitle'
import AttachmentRow from '../AttachmentRow'
import KibChatAssigneeSelect from '../KibChatAssigneeSelect'
import LinkToAppointmentSelect from './LinkToAppointmentSelect'

import styles from './KibChatHeader.module.scss'

const KibChatHeader = ({
  assigneeId,
  conversation,
  hasPatient,
}: {
  assigneeId: Field
  conversation: Conversation | Nil
  hasPatient: boolean
}) => {
  const dispatch = useDispatch()
  const isContextItem = useIsCurrentContextItem(conversation)
  const { title, transport, client: conversationClientId } = conversation || {}
  const patientName = useSelector(getPatientName(conversation?.patient))
  const isCvcRolesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_ROLES),
  )
  const isCvcSystemMessageSuppressionEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_SYSTEM_MESSAGE_SUPPRESSION),
  )
  const isLogicalSMSThreadingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.LOGICAL_SMS_THREADING),
  )
  const isSystemMessage =
    isCvcSystemMessageSuppressionEnabled &&
    Boolean(conversation?.isSystemMessage)
  const eventAppointmentPermissions = useSelector(
    getCRUDByArea(PermissionArea.EVENT_APPOINTMENT),
  )
  const attachments = useSelector(
    getFilesForMessageIds(conversation?.messages || []),
  )

  if (!conversation) {
    return (
      <KibContainer
        className={styles.headerContainer}
        data-testid="header-placeholder"
      />
    )
  }

  const setTitle = (newTitle: string) => {
    dispatch(editConversation(conversation.id, { title: newTitle }))
  }

  const handleAssigneeChange = (value: string) => {
    assigneeId.setValue(value)
    dispatch(setAssigneeForConversation(conversation.id, value))
  }

  return (
    <div className={styles.headerContainer}>
      <KibGrid className={styles.editableContent}>
        {isLogicalSMSThreadingEnabled ? (
          <KibEditableTitle
            endAdornment={
              <BusinessShareIcon
                businessIds={
                  conversation?.businessId ? [conversation.businessId] : []
                }
              />
            }
            readOnly={!isContextItem}
            setTitle={setTitle}
            title={title}
          />
        ) : (
          <KibGrid
            className={styles.transportIconContainer}
            data-testid="title-with-icon"
          >
            <KibGridItem>
              <TransportIcon
                classes={{ transportIcon: styles.transportIcon }}
                type={transport}
              />
            </KibGridItem>
            <EditableTitle
              endAdornment={
                <BusinessShareIcon
                  businessIds={
                    conversation?.businessId ? [conversation.businessId] : []
                  }
                />
              }
              readOnly={!isContextItem}
              setTitle={setTitle}
              title={title}
            />
          </KibGrid>
        )}
        <KibGrid className={styles.noGridItemMargins}>
          {hasPatient && (
            <KibGridItem data-testid="link-to-appointment-select">
              <LinkToAppointmentSelect
                clientId={conversationClientId}
                conversationId={conversation.id}
                currentAppointment={conversation?.event}
                disabled={
                  !isContextItem ||
                  (isCvcRolesEnabled && !eventAppointmentPermissions.read)
                }
                patientId={conversation?.patient}
                patientName={patientName}
              />
            </KibGridItem>
          )}
          <KibGridItem data-testid="user-select">
            <KibChatAssigneeSelect
              assigneeField={assigneeId}
              isContextItem={isContextItem}
              isSystemMessage={isSystemMessage}
              onAssigneeChange={handleAssigneeChange}
            />
          </KibGridItem>
        </KibGrid>
      </KibGrid>
      {Boolean(attachments.length) && (
        <AttachmentRow
          ariaLabelledBy={`attachment-group-for-conversation-${conversation.id}`}
          attachments={attachments}
        />
      )}
    </div>
  )
}

export default KibChatHeader
